import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Download, X, Smartphone, Monitor, Tablet } from 'lucide-react';

function InstallButton() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [deviceType, setDeviceType] = useState('desktop');

  useEffect(() => {
    // Detect device type
    const userAgent = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(userAgent)) {
      setDeviceType('ios');
    } else if (/android/.test(userAgent)) {
      setDeviceType('android');
    } else if (/tablet/.test(userAgent)) {
      setDeviceType('tablet');
    } else {
      setDeviceType('desktop');
    }

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      console.log('App already installed');
      return;
    }

    // For development: show button immediately for testing
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      setIsInstallable(true);
      setTimeout(() => setIsVisible(true), 3000);
      return;
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      setIsInstallable(true);
      // Show install button after 3 seconds
      setTimeout(() => setIsVisible(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for successful installation
    window.addEventListener('appinstalled', () => {
      console.log('PWA installed successfully');
      setIsInstallable(false);
      setIsVisible(false);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // For iOS, show instructions
      if (deviceType === 'ios') {
        alert('Pour installer sur iOS:\n1. Appuyez sur le bouton Partager\n2. Sélectionnez "Sur l\'écran d\'accueil"\n3. Appuyez sur "Ajouter"');
      }
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    } else {
      console.log('User dismissed the install prompt');
    }

    // Clear the deferredPrompt
    setDeferredPrompt(null);
    setIsVisible(false);
  };

  const handleDismiss = () => {
    setIsVisible(false);
    // Hide for 24 hours
    localStorage.setItem('installPromptDismissed', Date.now().toString());
  };

  // Check if user dismissed recently
  useEffect(() => {
    const dismissed = localStorage.getItem('installPromptDismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed);
      const now = Date.now();
      const hoursPassed = (now - dismissedTime) / (1000 * 60 * 60);
      
      if (hoursPassed < 24) {
        setIsVisible(false);
      }
    }
  }, []);

  // Don't show if not installable or not visible
  // if (!isInstallable && deviceType !== 'ios') return null;
  if (!isVisible) return null;

  const getDeviceIcon = () => {
    switch (deviceType) {
      case 'ios':
      case 'android':
        return <Smartphone size={20} />;
      case 'tablet':
        return <Tablet size={20} />;
      default:
        return <Monitor size={20} />;
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 animate-fadeIn">
      <div className="bg-gradient-to-r from-[#d03367] to-[#e04579] text-white rounded-2xl shadow-2xl p-4 max-w-sm relative">
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 text-white/80 hover:text-white transition"
        >
          <X size={18} />
        </button>

        <div className="flex items-start gap-3 mb-3">
          <div className="bg-white/20 p-2 rounded-lg">
            {getDeviceIcon()}
          </div>
          <div className="flex-1">
            <h4 className="font-bold text-sm mb-1">Installer Pilote Noodly</h4>
            <p className="text-xs text-white/90">
              {deviceType === 'ios' 
                ? 'Ajoutez l\'app à votre écran d\'accueil pour un accès rapide'
                : 'Installez l\'application sur votre appareil pour un accès rapide'}
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            onClick={handleInstallClick}
            className="flex-1 bg-white text-[#d03367] hover:bg-white/90 font-semibold"
            size="sm"
          >
            <Download className="mr-2" size={16} />
            Installer
          </Button>
          <Button
            onClick={handleDismiss}
            variant="ghost"
            className="text-white hover:bg-white/20"
            size="sm"
          >
            Plus tard
          </Button>
        </div>

        {deviceType === 'ios' && (
          <p className="text-xs text-white/70 mt-2 text-center">
            Appuyez sur <span className="font-semibold">Partager</span> puis <span className="font-semibold">Sur l'écran d'accueil</span>
          </p>
        )}
      </div>
    </div>
  );
}

export default InstallButton;
